import React, { useMemo } from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./barChart.module.scss";

interface BarConfig {
  dataKey: string;
  color: string;
  label: string;
}

interface BarChartProps {
  title?: string;
  data?: any[];
  xAxisKey?: string;
  bars?: BarConfig[];
  height?: number;
  showThisYear?: boolean;
  hideXAxisLabels?: boolean;
  onDownload?: () => void;
  excelFileName?: string;
}

const CustomBarChart: React.FC<BarChartProps> = ({
  title = "Bar Chart",
  data = [],
  xAxisKey = "year",
  bars = [
    { dataKey: "closed", color: "#08ADC2", label: "Closed" },
    { dataKey: "inProcess", color: "#5b6b9e", label: "In-Process" },
    { dataKey: "expired", color: "#ff6b6b", label: "Expired" },
  ],
  height = 300,
  showThisYear = false,
  hideXAxisLabels = false,
  onDownload,
  excelFileName = "Bar_Chart_Data",
}) => {
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    // Prepare columns dynamically based on bars configuration
    const columns = [
      { header: xAxisKey.charAt(0).toUpperCase() + xAxisKey.slice(1), key: xAxisKey, width: 25 },
      ...bars.map((bar) => ({
        header: bar.label,
        key: bar.dataKey,
        width: 15,
      })),
    ];

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns,
      data: data,
    };

    exportToExcel(excelData);
  };
  // Calculate optimal tick interval to show ~10-12 labels
  const xAxisInterval = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const dataLength = data.length;

    // Show all labels if data points are <= 12
    if (dataLength <= 12) return 0;

    // Calculate interval to show approximately 10-12 labels
    return Math.ceil(dataLength / 12) - 1;
  }, [data]);

  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!data || data.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    let max = 0;
    data.forEach((item) => {
      bars.forEach((bar) => {
        const value = item[bar.dataKey];
        if (typeof value === "number" && value > max) {
          max = value;
        }
      });
    });

    // Round up to nearest multiple of 10
    const roundedMax = Math.ceil(max / 10) * 10 + 20;
    const tickInterval = Math.ceil(roundedMax / 4 / 10) * 10;
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => i * tickInterval,
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [data, bars]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={`item-${index}`}
              className={styles.tooltipItem}
              style={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <Box className={styles.customLegend}>
        {bars.map((bar, index) => (
          <Box key={index} className={styles.legendItem}>
            <Box
              className={styles.legendDot}
              style={{ backgroundColor: bar.color }}
            />
            <Typography className={styles.legendText}>{bar.label}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#08ADC2",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={height}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#08ADC2",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          )}
          <IconButton
            onClick={handleDownload}
            size="small"
            sx={{
              color: "#5b6b9e",
              "&:hover": {
                backgroundColor: "rgba(91, 107, 158, 0.1)",
              },
            }}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={xAxisKey}
              tick={hideXAxisLabels ? false : { fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              interval={xAxisInterval}
            />
            <YAxis
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              domain={[0, maxValue]}
              ticks={ticks}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            {bars.map((bar, index) => (
              <Bar
                key={index}
                dataKey={bar.dataKey}
                fill={bar.color}
                radius={[24, 24, 0, 0]}
                barSize={20}
                name={bar.label}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <CustomLegend />
    </Box>
  );
};

export default CustomBarChart;
